import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Play, Lock, Check, Scroll, ArrowLeft, Award } from "lucide-react";

export default function CourseDetail() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);

  const active = user && (user.subscription?.status === "active" || user.role === "admin");
  const completed = new Set(user?.progress?.completed_lessons || []);
  const quizPassed = new Set(user?.progress?.quiz_passed || []);

  useEffect(() => { api.get(`/courses/${courseId}`).then((r) => setCourse(r.data)).catch(() => {}); }, [courseId]);

  if (!course) return <div className="min-h-screen pt-32 text-center text-text-muted">Gathering the teachings…</div>;

  const allDone = course.lessons.length > 0 && course.lessons.every((l) => completed.has(l.id));
  const quizOk = !course.has_quiz || quizPassed.has(courseId);
  const canCertify = active && allDone && quizOk;
  const progressPct = course.lessons.length ? Math.round((course.lessons.filter((l) => completed.has(l.id)).length / course.lessons.length) * 100) : 0;

  const openLesson = (lesson) => {
    if (!lesson.is_preview && !active) {
      navigate("/membership");
      return;
    }
    if (!user) { navigate("/login"); return; }
    navigate(`/courses/${courseId}/lessons/${lesson.id}`);
  };

  return (
    <div className="min-h-screen pt-24 pb-24 bg-sanctuary-deep">
      <div className="relative h-72 overflow-hidden">
        <img src={course.image} alt={course.title} className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-sanctuary-deep via-sanctuary-deep/70 to-transparent" />
        <div className="absolute bottom-8 left-0 right-0">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/courses" className="inline-flex items-center gap-2 text-text-muted hover:text-gold text-sm mb-4 transition-colors" data-testid="back-to-courses">
              <ArrowLeft size={14} /> All degrees
            </Link>
            <div className="flex items-center gap-4">
              <span className="font-jp text-5xl text-gold">{course.japanese_name}</span>
              <div>
                <div className="eyebrow mb-1">{course.level_label}</div>
                <h1 className="font-display text-4xl text-parchment">{course.title}</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 mt-10">
        <p className="text-text-muted leading-relaxed text-lg mb-8">{course.description}</p>

        {active && (
          <div className="mb-10">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-text-muted">Your progress</span>
              <span className="text-gold">{progressPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-sanctuary-card overflow-hidden">
              <motion.div className="h-full bg-gradient-to-r from-sage to-gold" initial={{ width: 0 }} animate={{ width: `${progressPct}%` }} transition={{ duration: 1 }} />
            </div>
          </div>
        )}

        <div className="eyebrow mb-5">The Teachings</div>
        <div className="space-y-3">
          {course.lessons.map((lesson, i) => {
            const isDone = completed.has(lesson.id);
            const locked = !lesson.is_preview && !active;
            return (
              <button
                key={lesson.id}
                onClick={() => openLesson(lesson)}
                className="w-full text-left group flex items-center gap-5 rounded-2xl bg-sanctuary-card gold-border p-5 hover:border-gold/40 transition-colors duration-300"
                data-testid={`lesson-item-${i + 1}`}
              >
                <div className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${isDone ? "bg-sage/20 text-sage-light" : locked ? "bg-sanctuary-surface text-text-dim" : "bg-gold/10 text-gold"}`}>
                  {isDone ? <Check size={18} /> : locked ? <Lock size={16} /> : <Play size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-text-dim text-xs">Teaching {i + 1}</span>
                    {lesson.is_preview && <span className="text-[0.6rem] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold/15 text-gold">Free preview</span>}
                  </div>
                  <div className="font-display text-xl text-parchment truncate">{lesson.title}</div>
                  <div className="text-sm text-text-muted truncate">{lesson.subtitle}</div>
                </div>
                <div className="flex items-center gap-3 text-text-dim shrink-0">
                  {lesson.pdf_url && <Scroll size={16} />}
                  <span className="text-sm text-gold opacity-0 group-hover:opacity-100 transition-opacity">{locked ? "Members only" : "Open"}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Ceremony + certificate */}
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {course.has_quiz && (
            <button
              onClick={() => active ? navigate(`/courses/${courseId}/quiz`) : navigate("/membership")}
              className="rounded-2xl bg-sanctuary-card gold-border p-6 text-left hover:border-gold/40 transition-colors group"
              data-testid="open-quiz-button"
            >
              <div className="w-10 h-10 rounded-full bg-gold/10 grid place-items-center text-gold mb-4"><Award size={18} /></div>
              <div className="font-display text-xl text-parchment mb-1">Attunement Ceremony</div>
              <p className="text-sm text-text-muted">{quizPassed.has(courseId) ? "You have passed this ceremony." : "A mindful reflection to confirm your readiness."}</p>
            </button>
          )}
          <button
            onClick={() => canCertify ? navigate(`/certificate/${courseId}`) : null}
            disabled={!canCertify}
            className={`rounded-2xl p-6 text-left transition-colors ${canCertify ? "bg-gold/10 border border-gold/40 hover:bg-gold/15" : "bg-sanctuary-card gold-border opacity-60 cursor-not-allowed"}`}
            data-testid="view-certificate-button"
          >
            <div className="w-10 h-10 rounded-full bg-gold/10 grid place-items-center text-gold mb-4"><Scroll size={18} /></div>
            <div className="font-display text-xl text-parchment mb-1">Certificate of Attunement</div>
            <p className="text-sm text-text-muted">{canCertify ? "Receive your sacred seal." : "Complete all teachings and the ceremony to unlock."}</p>
          </button>
        </div>
      </div>
    </div>
  );
}
